// Web-driven Sales Navigator sync poller (ADR 0032 follow-up).
//
// Pre-existing shape (untouched):
//   The extension runs `pollSalesNav()` on a `chrome.alarms` timer
//   (~every 60s) inside `background.js`. That continues to work
//   stand-alone; nothing below shuts it off.
//
// What this hook adds:
//   A *second* driver of the same sync, scheduled by the web app's
//   polling framework (ADR 0024). Difference vs the extension alarm:
//
//     * Cross-tab coordination via the tab lock — at most one open
//       web tab ticks this poller at a time, regardless of how many
//       tabs the user has open.
//     * Cursor persisted to Supabase `sync_cursors('salesnav_inbox')`
//       so it survives extension reinstalls and is visible across
//       devices.
//     * Visibility gate — skip ticks when the tab is hidden (already
//       enforced by the framework).
//     * API-temperature gate — back off when LinkedIn's rate-limit
//       temperature is MEDIUM+ (ADR 0021).
//     * Feature-flag gate — `SALES_NAV` (currently `defaultOn: true`).
//
// If the user never opens the web app, the extension alarm keeps the
// inbox fresh the way it always has. The web poller only adds value
// when the web app is open.
//
// Design trade-off — double-polling: with both the extension alarm
// and this web poller active, the Sales Nav endpoint can be hit at
// roughly 2× the old cadence in active tabs. The extension's own
// `_lastActivityMap` change detection makes the second call a cheap
// no-op when nothing's newer; we accept that. If LinkedIn ever starts
// rate-limiting tighter, flip the extension alarm off or bump the web
// poller interval.
//
// ADR 0032 §"Not in this commit" previously listed this hook as
// follow-up work. It's the 1.9 Sales Navigator integration becoming
// cursor-driven end-to-end.

import { useEffect, useMemo } from 'react'
import { createPollingManager, type PollingManager } from '../lib/polling'
import { useSyncCursor, type SyncCursor } from './useSyncCursor'
import { useAuth } from '../lib/auth'
import { sendCommand } from '../lib/extension'
import { isFeatureOn } from '../lib/features'
import { createLogger } from '../lib/logger'
import { useLatestRef } from './useLatestRef'

const log = createLogger('salesnav-poller')

/** Cursor persisted to `sync_cursors('salesnav_inbox')`. Kept small
 * + provider-specific shape; nothing outside this hook reads it. */
interface SalesNavInboxCursor {
  /** `pageStartsAt` timestamp from the last `SALES_FETCH_CONVERSATIONS`
   * response — seeds the next fetch so we can resume pagination if
   * we ever grow past the first-page worth of threads. When null,
   * the next tick starts from "now" (most recent page). */
  pageStartsAt: number | null
  /** The newest `lastMessageTimestamp` observed across any synced
   * conversation. Informational for now — not used for filtering
   * (the extension's in-memory change detection does that) but
   * persisting it gives us "last activity we know of" when
   * diagnosing stalls. */
  latestActivityTs: number
}

/** Fetch-response shape from the extension's `SALES_FETCH_CONVERSATIONS`
 * handler. Narrowed to the fields we read. */
interface SalesFetchResult {
  conversations: Array<{
    conversationUrn: string | null
    lastMessageTimestamp?: number
  }>
  nextPageStartsAt: number | null
}

const DEFAULT_CURSOR: SalesNavInboxCursor = {
  pageStartsAt: null,
  latestActivityTs: 0,
}

// Normal-path interval. Active Sales Nav use doesn't warrant
// hammering — the extension alarm already ticks at ~60s. Picking
// a slightly off-aligned multiple so the two drivers don't fire
// in lock-step.
const INTERVAL_MS = 73_000

/**
 * Mount this once in Shell. No-op when `hasSalesNav` is false, the
 * `SALES_NAV` feature flag is off, or the user isn't authenticated.
 * Safe to re-mount (the polling manager's `start()` is idempotent
 * and the tab lock coordinates across mounts anyway).
 */
export function useSalesNavPoller({ hasSalesNav }: { hasSalesNav: boolean }): void {
  const { user } = useAuth()
  const cursor = useSyncCursor('salesnav_inbox')

  // Pin the cursor advance function — identity must be stable across
  // renders or the polling manager would re-create itself mid-tick.
  // `useSyncCursor` already memoises its mutation via TanStack Query,
  // so the callback reference is stable per-user.
  const advance = cursor.advance

  // Live-cursor capture via the shared `useLatestRef` helper. Fixes
  // the stale-closure bug from PR 3 of the code-review handoff: the
  // polling manager is memoised on `[user?.id, advance]` to keep its
  // identity stable, but the `onTick` closure used to read
  // `cursor.cursor?.cursor` directly — capturing the value from the
  // render where the memo was created. After `useSyncCursor`
  // refetched with a new cursor (e.g. pagination advanced past page
  // 1), the tick kept seeing the old value. The ref is updated
  // every render so ticks always see the latest.
  const cursorRef = useLatestRef<SyncCursor | null>(cursor.cursor)

  const manager: PollingManager | null = useMemo(() => {
    if (!user) return null
    return createPollingManager({
      name: 'salesnav-inbox',
      interval: INTERVAL_MS,
      requiredFeatures: ['SALES_NAV'],
      // Tab-lock scoped per user so two signed-in users in different
      // profiles don't share a lock.
      requireLock: `salesnav-poll:${user.id}`,
      requireVisible: true,
      backoffOnTemperature: true,
      onTick: async () => {
        // Snapshot whatever's in the cursor right now — read via ref
        // so this sees the LATEST value, not the one captured when
        // the manager was memoised. If none has been persisted yet,
        // we start fresh with "most recent page" semantics
        // (pageStartsAt=null → extension defaults to Date.now()).
        const persisted = (
          (cursorRef.current?.cursor ?? {}) as Partial<SalesNavInboxCursor>
        )
        const prev: SalesNavInboxCursor = {
          pageStartsAt: typeof persisted.pageStartsAt === 'number' ? persisted.pageStartsAt : DEFAULT_CURSOR.pageStartsAt,
          latestActivityTs: typeof persisted.latestActivityTs === 'number' ? persisted.latestActivityTs : DEFAULT_CURSOR.latestActivityTs,
        }

        try {
          // Mark "running" so other tabs / admin dashboards can see
          // we're mid-sync. Cursor fields preserved so a mid-tick
          // cancellation rolls back cleanly.
          await advance({ cursor: prev, status: 'running' })

          const result = await sendCommand<SalesFetchResult>(
            'SALES_FETCH_CONVERSATIONS',
            { pageStartsAt: prev.pageStartsAt ?? undefined },
          )

          const nextLatest = (result.conversations ?? []).reduce<number>(
            (max, c) => {
              const ts = typeof c.lastMessageTimestamp === 'number' ? c.lastMessageTimestamp : 0
              return ts > max ? ts : max
            },
            prev.latestActivityTs,
          )

          const next: SalesNavInboxCursor = {
            pageStartsAt: result.nextPageStartsAt ?? prev.pageStartsAt,
            latestActivityTs: nextLatest,
          }

          await advance({ cursor: next, status: 'idle', error: null })
          log.debug('tick ok', {
            fetched: result.conversations?.length ?? 0,
            pageStartsAt: next.pageStartsAt,
            latestActivityTs: next.latestActivityTs,
          })
        } catch (err) {
          const msg = (err instanceof Error ? err.message : String(err)).slice(0, 500)
          // Preserve the prior cursor on error — next tick retries from
          // the same point. Status surfaces the failure to anyone
          // reading the row.
          await advance({ cursor: prev, status: 'error', error: msg })
          log.warn('tick failed', { msg })
          // Re-throw so the polling framework counts this as a failed
          // tick and can apply its own backoff.
          throw err
        }
      },
    })
  // Deps: only `user?.id` and `advance` — `cursor` is read via
  // `cursorRef` inside `onTick`, so including it would just thrash
  // the manager identity on every refetch. The ref pattern is the
  // correct idiom here; no exhaustive-deps suppression needed.
  }, [user?.id, advance])

  useEffect(() => {
    if (!manager) return
    if (!hasSalesNav) {
      manager.stop()
      return
    }
    // The framework also gates on feature flag + visibility + lock
    // internally; this outer gate just saves the `start()` when we
    // know there's nothing to do.
    if (!isFeatureOn('SALES_NAV')) {
      manager.stop()
      return
    }
    manager.start()
    return () => manager.stop()
  }, [manager, hasSalesNav])
}
